import { LocationData } from '@/types/locations';
import { AdSpace, AdSpaceWithFactorPricingScheme, AdSpaceWithFrequencyPricingScheme } from '@/types/adspaces';
import { CampaignFrequency } from '@/types/campaigns';
import { priceByFrequency } from '@/utils/numbers';
import { FREQUENCY_PRICES } from '@/statics/ad-spaces';
import { compact, flatMap, isString, split, filter, isNil, trim } from 'lodash';

export const sortLocationsByPriorityOfAppearance = (locations: LocationData[]) => {
  return locations.sort((a, b) => {
    if (a.PRIORITY === undefined) return 1;
    if (b.PRIORITY === undefined) return -1;
    return a.PRIORITY - b.PRIORITY;
  });
}

function isFactorPricing(adSpace: AdSpace): adSpace is AdSpaceWithFactorPricingScheme {
  return !adSpace.IS_FREQUENCY_PRICING && Boolean(adSpace.BASE_PRICE);
}

// Type guard for AdSpaceWithFrequencyPricingScheme
function isFrequencyPricing(adSpace: AdSpace): adSpace is AdSpaceWithFrequencyPricingScheme {
  return Boolean(adSpace.IS_FREQUENCY_PRICING) && Boolean(adSpace.FREQUENCY_PRICING);
}

/**
 * Calculates the price for an ad space based on the selected frequency
 * @param adSpace - The ad space containing pricing information
 * @param selectedFrequency - The campaign frequency selected by the user
 * @returns The calculated price based on frequency, or 0 if no pricing is available
 */
export const getLocationPriceByFrequency = (adSpace: AdSpace, selectedFrequency?: CampaignFrequency): number => {
  if (!selectedFrequency)
    return 0
  if (isFactorPricing(adSpace)) {
    return priceByFrequency(adSpace.BASE_PRICE, selectedFrequency);
  } else if (isFrequencyPricing(adSpace)) {
    const frequencyKey = FREQUENCY_PRICES[selectedFrequency.NAME];
    return adSpace.FREQUENCY_PRICING[frequencyKey];
  }

  return 0;
};

// ================= CENTRALIZED LOCATION FILTERING UTILITIES =================

export interface LocationFilterCriteria {
  selectedCities?: string[];
  selectedLocationTypes?: string[];
}

/**
 * Extracts string array from route query parameters
 * Handles comma-separated values: "a,b,c" -> ["a", "b", "c"]
 */
const extractStringArray = (value: unknown): string[] => {
  if (isNil(value)) return [];

  // Step 1: Normalize input to string array using Lodash
  const stringArray = isString(value)
    ? [value]
    : filter(value as any[], isString);

  // Step 2: Split comma-separated values and clean up
  return compact(
    flatMap(stringArray, str =>
      split(str, ',').map(trim)
    )
  );
};
/**
 * Converts URL query parameters to filter criteria
 */
export const queryToFilterCriteria = (routeQuery: Record<string, any>): LocationFilterCriteria => {

  const criteria: LocationFilterCriteria = {};

  const cities = extractStringArray(routeQuery.cities);
  if (cities.length > 0) {
    criteria.selectedCities = cities;
  }

  const types = extractStringArray(routeQuery.types);
  if (types.length > 0) {
    criteria.selectedLocationTypes = types;
  }
  return criteria;
};

/**
 * Filters locations based on cities and location types with O(1) lookup complexity using reduce
 */
export const filterLocations = (
  locations: LocationData[],
  criteria: LocationFilterCriteria
): LocationData[] => {
  // If no criteria, return all locations
  if (!criteria.selectedCities?.length && !criteria.selectedLocationTypes?.length) {
    return locations;
  }

  // Create lookup objects using reduce
  const cityLookup = criteria.selectedCities?.reduce((cityLookupMap, city) => {
    cityLookupMap[city] = true;
    return cityLookupMap;
  }, {} as Record<string, boolean>) || {};

  const typeLookup = criteria.selectedLocationTypes?.reduce((typeLookupMap, typeVal) => {
    typeLookupMap[typeVal] = true;
    return typeLookupMap;
  }, {} as Record<string, boolean>) || {};

  // Filter and return
  return locations.filter(location => {
    const cityMatches = Object.keys(cityLookup).length === 0 || cityLookup[location.CITY.GEONAME_ID];
    const typeMatches = Object.keys(typeLookup).length === 0 || typeLookup[location.TYPE.VAL];
    return cityMatches && typeMatches;
  });
};

/**
 * Converts filter criteria to URL query object (for Vue Router)
 */
export const filterCriteriaToQuery = (criteria: LocationFilterCriteria): Record<string, string> => {

  const query: Record<string, string> = {};

  if (criteria.selectedCities?.length) {
    query.cities = criteria.selectedCities.join(',');
  }

  if (criteria.selectedLocationTypes?.length) {
    query.types = criteria.selectedLocationTypes.join(',');
  }

  return query;
};

/**
 * Gets all valid city IDs from locations using reduce
 */
const getValidCityIds = (locations: LocationData[]): Record<string, boolean> =>
  locations.reduce((map, location) => ({ ...map, [location.CITY.GEONAME_ID]: true }), {});

/**
 * Gets all valid location type values from locations using reduce
 */
const getValidTypeValues = (locations: LocationData[]): Record<string, boolean> =>
  locations.reduce((map, location) => ({ ...map, [location.TYPE.VAL]: true }), {});

/**
 * Validates city IDs against available locations
 */
export const validateCityIds = (cityIds: string[], locations: LocationData[]) => {
  const validIds = getValidCityIds(locations);
  const valid = cityIds.filter(id => validIds[id]);
  const invalid = cityIds.filter(id => !validIds[id]);
  return { valid, invalid };
};

/**
 * Validates location types against available locations
 */
export const validateLocationTypes = (typeValues: string[], locations: LocationData[]) => {
  const validTypes = getValidTypeValues(locations);
  const valid = typeValues.filter(type => validTypes[type]);
  const invalid = typeValues.filter(type => !validTypes[type]);
  return { valid, invalid };
};

/**
 * Validates and cleans filter criteria against available locations
 */
export const validateFilterCriteria = (
  criteria: LocationFilterCriteria,
  locations: LocationData[]
): LocationFilterCriteria => {
  const cleanedCriteria: LocationFilterCriteria = {};
  if (criteria.selectedCities?.length) {
    const { valid } = validateCityIds(criteria.selectedCities, locations);
    if (valid.length > 0) {
      cleanedCriteria.selectedCities = valid;
    }
  }

  if (criteria.selectedLocationTypes?.length) {
    const { valid } = validateLocationTypes(criteria.selectedLocationTypes, locations);
    if (valid.length > 0) {
      cleanedCriteria.selectedLocationTypes = valid;
    }
  }
  return cleanedCriteria;
};

/**
 * Simple check if route has valid location filters
 * This function should ONLY receive route query parameters
 */
export const hasValidRouteFilters = (routeQuery: Record<string, any>, locations: LocationData[]): boolean => {

  if (!routeQuery.cities && !routeQuery.types) {
    return false;
  }

  // Convert route query to criteria
  const criteria = queryToFilterCriteria(routeQuery);

  // Validate the criteria
  const validatedCriteria = validateFilterCriteria(criteria, locations);

  return !!(validatedCriteria.selectedCities?.length || validatedCriteria.selectedLocationTypes?.length);
};


